import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import ThemeToggle from './ThemeToggle';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/#services', label: t('nav.services') },
    { href: '/#industries', label: t('nav.industries') },
    { href: '/#reference', label: t('nav.reference') },
    { href: '/#about', label: t('nav.about') },
    { href: '/faq', label: t('nav.faq') },
    { href: '/blog', label: t('nav.blog') },
    { href: '/#contact', label: t('nav.contact') }
  ];

  const handleNavClick = (e, href) => {
    setIsMobileMenuOpen(false);

    // Regular page links - let default behavior handle it
    if (!href.startsWith('/#')) return;

    e.preventDefault();
    const sectionId = href.replace('/#', '');

    // If on homepage, smooth scroll
    if (location.pathname === '/') {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Navigate to homepage, then scroll after load
      navigate('/', { state: { scrollTo: sectionId } });
    }
  };

  const handleContactClick = () => {
    setIsMobileMenuOpen(false);
    if (location.pathname === '/') {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/', { state: { scrollTo: 'contact' } });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.jpg" alt="Design Vision" className="w-9 h-9 rounded-lg object-cover" />
            <span className="font-display text-2xl font-bold tracking-tight text-black dark:text-white">
              Design<span className="text-mid-purple">Vision</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-sm font-medium text-dark-grey dark:text-gray-300 hover:text-black dark:hover:text-white transition-colors relative group"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-mid-purple transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            <ThemeToggle />
            <Button
              className="bg-black dark:bg-white hover:bg-black/90 dark:hover:bg-gray-100 text-white dark:text-black rounded-full px-6 font-mono text-xs uppercase tracking-wider"
              onClick={handleContactClick}
            >
              {t('nav.contact')}
            </Button>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
            <button
              className="p-2 text-black dark:text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-white dark:bg-gray-900 border-t dark:border-gray-800">
          <nav className="flex flex-col p-6 gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-base font-medium text-dark-grey dark:text-gray-300 hover:text-black dark:hover:text-white transition-colors py-2"
              >
                {link.label}
              </a>
            ))}
            <Button
              className="bg-black dark:bg-white hover:bg-black/90 dark:hover:bg-gray-100 text-white dark:text-black rounded-full mt-4 font-mono text-xs uppercase tracking-wider"
              onClick={handleContactClick}
            >
              {t('nav.contact')}
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
